import { useState } from "react";
import { KeyboardArrowLeftRounded, KeyboardArrowRightRounded } from "@mui/icons-material";
import { Box, Card, Divider, Typography } from "@mui/joy";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { BadgePlus, House } from "lucide-react";

import { Alert, Button, ProgressStepper } from "../../../../shared";

// Import step components
import { BasicDetails } from "./BasicDetails";
import { CustomerDetails } from "./CustomerDetails";
import { InsuranceDetails } from "./InsuranceDetails";
import { DateEstimation } from "./DateEstimation";
import { PropertyDetails } from "./PropertyDetails";
import { useCreateProjectHandler } from "./hooks";

import { defaultValues, steps } from "./variables";
import { newProjectValidation } from "../../../../libs/form-validations";

export default function NewProject() {
    const navigate = useNavigate();
    const { createProjectEventHandler, progress } = useCreateProjectHandler();

    const methods = useForm({
        mode: "onChange",
        defaultValues,
        resolver: newProjectValidation,
    });

    const {
        handleSubmit,
        trigger,
        formState: { isValid },
    } = methods;
    const [currentStep, setCurrentStep] = useState(0);

    const onSubmit = async (data: any) => {
        try {
            const { projectId } = await createProjectEventHandler(data);
            if (projectId) {
                navigate(`/project/list/${projectId}`);
            }
        } catch (e) {
            console.error("Project Creation Error:", e);
        }
    };

    // 2️⃣ Next handler with per-step validation
    const handleNext = async () => {
        const fieldsToValidate = steps[currentStep].fields as any;
        const isStepValid = await trigger(fieldsToValidate);
        if (isStepValid) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    // 3️⃣ Previous step handler
    const handlePrevious = () => {
        setCurrentStep((prev) => prev - 1);
    };

    const handleClickOnProgressBar = (activeStep: any) => {
        const currentIndex = steps.findIndex((step: any) => step.label === activeStep.label);
        setCurrentStep(currentIndex);
    };

    return (
        <Box m={2} mx={3} width={800}>
            <Box display={"flex"} gap={0.8} alignItems={"center"} mb={1}>
                <Card sx={{ p: 0.5, borderRadius: "6px" }}>
                    <BadgePlus size={14} />
                </Card>
                <Typography level="title-lg">New Project</Typography>
            </Box>
            <Typography level="body-sm">
                Please provide the details below to help us better understand your needs and get started on delivering
                the perfect roofing solution for your home or business.
            </Typography>

            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {/* Stepper */}
                    <Box mt={2}>
                        <Divider sx={{ mb: 2 }} />
                        <ProgressStepper currentStep={currentStep} steps={steps} onClick={handleClickOnProgressBar} />
                        <Divider sx={{ mt: 2 }} />
                    </Box>

                    {/* Current Step Content */}
                    <Box mt={1} width={"100%"}>
                        {currentStep === 0 && <BasicDetails />}
                        {currentStep === 1 && <CustomerDetails />}
                        {currentStep === 2 && <PropertyDetails />}
                        {currentStep === 3 && <DateEstimation />}
                        {currentStep === 4 && <InsuranceDetails />}
                    </Box>

                    {/* Navigation Buttons */}
                    <Box mt={3} display="flex" alignItems="center" gap={1} pb={3}>
                        <Button
                            startDecorator={currentStep === 0 ? <House size={18} /> : <KeyboardArrowLeftRounded />}
                            onClick={handlePrevious}
                            variant="soft"
                            type="button"
                        >
                            {currentStep === 0 ? "Home" : "Previous"}
                        </Button>

                        {currentStep < steps.length - 1 && (
                            <Button endDecorator={<KeyboardArrowRightRounded />} onClick={handleNext} type="button">
                                {steps[currentStep + 1].label}
                            </Button>
                        )}

                        {currentStep === steps.length - 1 && (
                            <Button type="submit" loading={progress.loading} loadingPosition="end" disabled={!isValid}>
                                {progress.loading ? "Project creation underway!" : "Create Project"}
                            </Button>
                        )}
                    </Box>

                    {progress.loading && (
                        <Typography sx={{ mt: 2 }} level="title-sm">
                            {progress.message}
                        </Typography>
                    )}

                    {progress.error && (
                        <Box mt={2}>
                            <Alert errorMessage={progress.error} />
                        </Box>
                    )}
                </form>
            </FormProvider>
        </Box>
    );
}
